import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileText, Landmark, KeyRound, Network, Pencil, Plus, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadingState } from '@/components/ui/loading-state';
import { EmptyState } from '@/components/ui/empty-state';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useStandort } from '@/components/layouts/use-standort';
import { usePermission } from '@/components/rbac/use-permission';
import { P } from '@/lib/rbac/permissions.generated';
import EbicsSetupDialog from './EbicsSetupDialog';
import {
  billingApi,
  type BillingEntity,
  type BillingEntityInput,
  type BillingModel,
  type BillingSettings,
  type CancellationRule,
  type CheckoutProvider,
  type DocumentKind,
  type PeriodAnchor,
  type TaxMode,
} from '@/lib/api/billing';
import {
  BILLING_MODEL_HINT,
  BILLING_MODEL_LABEL,
  CANCELLATION_RULE_LABEL,
  CHECKOUT_PROVIDER_LABEL,
  INTERVALS,
  INTERVAL_LABEL,
  MONTH_LABEL,
  PERIOD_ANCHOR_LABEL,
  SEPA_PROBLEM_LABEL,
  TAX_MODE_LABEL,
  creditorIdProblem,
  formatIban,
  ibanProblem,
  parseEuroToCents,
} from '@/lib/billing';

const ADMIN = P.admin.zahlungswesen_und_integrationen;
const MODELS: BillingModel[] = ['periodic', 'contingent'];
const NO_TAX_MODE = 'none';

const toggle = <T,>(list: readonly T[], value: T, checked: boolean): T[] =>
  checked ? Array.from(new Set([...list, value])) : list.filter((item) => item !== value);

export default function BillingSettingsPage() {
  const { standorte } = useStandort();
  const canConfigureParams = usePermission(ADMIN.zahlungsparameter_konfigurieren);
  const canConfigure = usePermission(ADMIN.zahlungsintegration_konfigurieren);

  const [settings, setSettings] = useState<BillingSettings | null>(null);
  const [entities, setEntities] = useState<BillingEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [editing, setEditing] = useState<BillingEntity | 'new' | null>(null);
  const [credentialsFor, setCredentialsFor] = useState<BillingEntity | null>(null);
  const [sepaFor, setSepaFor] = useState<BillingEntity | null>(null);
  const [documentsFor, setDocumentsFor] = useState<BillingEntity | null>(null);
  const [ebicsFor, setEbicsFor] = useState<BillingEntity | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [loadedSettings, loadedEntities] = await Promise.all([
        billingApi.getSettings(),
        billingApi.listEntities(),
      ]);
      setSettings(loadedSettings);
      setEntities(Array.isArray(loadedEntities?.items) ? loadedEntities.items : []);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Abrechnung konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const entityOfStandort = useMemo(() => {
    const map = new Map<number, BillingEntity>();
    for (const entity of entities) {
      for (const standortId of entity.standortIds ?? []) map.set(standortId, entity);
    }
    return map;
  }, [entities]);

  const saveSettings = async () => {
    if (!settings) return;
    setSavingSettings(true);
    try {
      setSettings(await billingApi.saveSettings(settings));
      toast.success('Abrechnungsmodell gespeichert');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Speichern fehlgeschlagen');
    } finally {
      setSavingSettings(false);
    }
  };

  const assignStandort = async (standortId: number, entityId: string) => {
    try {
      await billingApi.assignStandort(standortId, Number(entityId));
      toast.success('Standort zugeordnet');
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Zuordnung fehlgeschlagen');
    }
  };

  if (loading) return <LoadingState message="Abrechnung wird geladen …" />;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Abrechnung</h1>
        <p className="text-muted-foreground">
          Wie dieser Mandant abrechnet und in wessen Namen eingezogen wird.
        </p>
      </div>

      {settings && (
        <Card>
          <CardHeader>
            <CardTitle>Abrechnungsmodell</CardTitle>
            <CardDescription>
              Gilt für neue Verträge. Tarife können das Modell je Unterrichtsform überschreiben; laufende
              Verträge behalten ihr Modell.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <fieldset className="space-y-3" disabled={!canConfigureParams}>
              <legend className="text-sm font-medium">Aktive Modelle</legend>
              {MODELS.map((model) => (
                <label key={model} className="flex items-start gap-3 rounded-lg border p-3">
                  <Checkbox
                    id={`model-${model}`}
                    checked={settings.enabledModels.includes(model)}
                    onCheckedChange={(checked) =>
                      setSettings({
                        ...settings,
                        enabledModels: toggle(settings.enabledModels, model, checked === true),
                      })
                    }
                  />
                  <span>
                    <span className="block font-medium">{BILLING_MODEL_LABEL[model]}</span>
                    <span className="block text-sm text-muted-foreground">{BILLING_MODEL_HINT[model]}</span>
                  </span>
                </label>
              ))}
            </fieldset>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="default-model">Standard für neue Verträge</Label>
                <Select
                  value={settings.defaultModel}
                  onValueChange={(value) => setSettings({ ...settings, defaultModel: value as BillingModel })}
                  disabled={!canConfigureParams}
                >
                  <SelectTrigger id="default-model">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {settings.enabledModels.map((model) => (
                      <SelectItem key={model} value={model}>
                        {BILLING_MODEL_LABEL[model]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cancellation-rule">Stundenausfall beim Beitrag</Label>
                <Select
                  value={settings.lessonCancellationRule}
                  onValueChange={(value) =>
                    setSettings({ ...settings, lessonCancellationRule: value as CancellationRule })
                  }
                  disabled={!canConfigureParams}
                >
                  <SelectTrigger id="cancellation-rule">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CANCELLATION_RULE_LABEL).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {settings.enabledModels.includes('periodic') && (
              <div className="grid gap-4 md:grid-cols-2">
                <fieldset className="space-y-2" disabled={!canConfigureParams}>
                  <legend className="text-sm font-medium">Erlaubte Zahlweisen</legend>
                  <div className="flex flex-wrap gap-4">
                    {INTERVALS.map((interval) => (
                      <label key={interval} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={settings.allowedIntervals.includes(interval)}
                          onCheckedChange={(checked) =>
                            setSettings({
                              ...settings,
                              allowedIntervals: toggle(settings.allowedIntervals, interval, checked === true).sort(
                                (a, b) => a - b,
                              ),
                            })
                          }
                        />
                        {INTERVAL_LABEL[interval]}
                      </label>
                    ))}
                  </div>
                </fieldset>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="period-anchor">Bezugsjahr</Label>
                    <Select
                      value={settings.periodAnchor}
                      onValueChange={(value) => setSettings({ ...settings, periodAnchor: value as PeriodAnchor })}
                      disabled={!canConfigureParams}
                    >
                      <SelectTrigger id="period-anchor">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(PERIOD_ANCHOR_LABEL).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {settings.periodAnchor === 'school_year' && (
                    <div className="space-y-2">
                      <Label htmlFor="school-year-start">Schuljahr beginnt im</Label>
                      <Select
                        value={String(settings.schoolYearStartMonth ?? 8)}
                        onValueChange={(value) => setSettings({ ...settings, schoolYearStartMonth: Number(value) })}
                        disabled={!canConfigureParams}
                      >
                        <SelectTrigger id="school-year-start">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MONTH_LABEL.map((label, index) => (
                            <SelectItem key={label} value={String(index + 1)}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>
            )}

            {canConfigureParams && (
              <div className="flex justify-end">
                <Button onClick={saveSettings} disabled={savingSettings || settings.enabledModels.length === 0}>
                  <Save className="mr-2 h-4 w-4" />
                  {savingSettings ? 'Speichert …' : 'Speichern'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div className="space-y-1.5">
            <CardTitle>Abrechnende Einheiten</CardTitle>
            <CardDescription>
              Gläubiger und Rechnungssteller. Bei Franchise ist das der Inhaber eines oder mehrerer Standorte.
            </CardDescription>
          </div>
          {canConfigure && (
            <Button onClick={() => setEditing('new')}>
              <Plus className="mr-2 h-4 w-4" />
              Einheit anlegen
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {entities.length === 0 ? (
            <EmptyState message="Noch keine abrechnende Einheit angelegt." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Einheit</TableHead>
                  <TableHead>Standorte</TableHead>
                  <TableHead>Besteuerung</TableHead>
                  <TableHead>Stripe</TableHead>
                  <TableHead>Lastschrift</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entities.map((entity) => {
                  const stripe = entity.providers?.stripe ?? {};
                  const names = standorte
                    .filter((standort) => entity.standortIds?.includes(standort.id))
                    .map((standort) => standort.name);
                  return (
                    <TableRow key={entity.id}>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-2 font-medium">
                          {entity.name}
                          {entity.isDefault && <Badge variant="info" size="sm">Standard</Badge>}
                          {!entity.isActive && <Badge variant="secondary" size="sm">Inaktiv</Badge>}
                        </div>
                        {entity.legalName && <div className="text-sm text-muted-foreground">{entity.legalName}</div>}
                      </TableCell>
                      <TableCell className="text-sm">{names.length > 0 ? names.join(', ') : '–'}</TableCell>
                      <TableCell className="text-sm">
                        {entity.taxMode ? TAX_MODE_LABEL[entity.taxMode] : (
                          <span className="text-warning-foreground">Nicht festgelegt</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {stripe.error ? (
                          <Badge variant="destructive" size="sm">Schlüssel fehlt</Badge>
                        ) : stripe.secretKeyIsSet ? (
                          <Badge variant="success" size="sm">Eigenes Konto</Badge>
                        ) : (
                          <span className="text-muted-foreground">Globales Konto</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {entity.sepaProblem ? (
                          <span className="text-muted-foreground">
                            {SEPA_PROBLEM_LABEL[entity.sepaProblem] ?? 'Nicht eingerichtet'}
                          </span>
                        ) : (
                          <Badge variant="success" size="sm">Bereit</Badge>
                        )}
                        {entity.checkoutProvider && (
                          <div className="text-xs text-muted-foreground">
                            Website: {entity.checkoutProvider === 'hausbank_sepa' ? 'Hausbank' : 'Stripe'}
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground">
                          Bank: {entity.sepaTransport === 'ebics' ? 'EBICS' : 'Datei-Upload'}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {canConfigure && (
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => setSepaFor(entity)}>
                              <Landmark className="mr-2 h-4 w-4" />
                              Bankverbindung
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setEbicsFor(entity)}>
                              <Network className="mr-2 h-4 w-4" />
                              EBICS
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setDocumentsFor(entity)}>
                              <FileText className="mr-2 h-4 w-4" />
                              Belege &amp; Mahnwesen
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setCredentialsFor(entity)}>
                              <KeyRound className="mr-2 h-4 w-4" />
                              Stripe
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setEditing(entity)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Bearbeiten
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {entities.length > 0 && standorte.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Standorte zuordnen</CardTitle>
            <CardDescription>
              Auf welches Konto die Verträge eines Standorts laufen. Bestehende Vertragskonten behalten ihre
              Einheit.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Standort</TableHead>
                  <TableHead className="w-72">Abrechnende Einheit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {standorte.map((standort) => (
                  <TableRow key={standort.id}>
                    <TableCell className="font-medium">{standort.name}</TableCell>
                    <TableCell>
                      <Select
                        value={entityOfStandort.get(standort.id)?.id?.toString() ?? ''}
                        onValueChange={(value) => void assignStandort(standort.id, value)}
                        disabled={!canConfigure}
                      >
                        <SelectTrigger aria-label={`Einheit für ${standort.name}`}>
                          <SelectValue placeholder="Standard-Einheit" />
                        </SelectTrigger>
                        <SelectContent>
                          {entities
                            .filter((entity) => entity.isActive)
                            .map((entity) => (
                              <SelectItem key={entity.id} value={String(entity.id)}>
                                {entity.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {editing !== null && (
        <EntityDialog
          entity={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
      {sepaFor && (
        <SepaDialog
          entity={sepaFor}
          onClose={() => setSepaFor(null)}
          onSaved={async () => {
            setSepaFor(null);
            await load();
          }}
        />
      )}
      {ebicsFor && (
        <EbicsSetupDialog entity={ebicsFor} onClose={() => setEbicsFor(null)} onChanged={load} />
      )}

      {documentsFor && (
        <DocumentSettingsDialog
          entity={documentsFor}
          onClose={() => setDocumentsFor(null)}
          onSaved={async () => {
            setDocumentsFor(null);
            await load();
          }}
        />
      )}
      {credentialsFor && (
        <StripeCredentialsDialog
          entity={credentialsFor}
          onClose={() => setCredentialsFor(null)}
          onSaved={async () => {
            setCredentialsFor(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

interface EntityDialogProps {
  entity: BillingEntity | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

function EntityDialog({ entity, onClose, onSaved }: EntityDialogProps) {
  const [form, setForm] = useState<BillingEntityInput>(() => ({
    name: entity?.name ?? '',
    legalName: entity?.legalName ?? '',
    street: entity?.street ?? '',
    zip: entity?.zip ?? '',
    city: entity?.city ?? '',
    country: entity?.country ?? 'DE',
    taxNumber: entity?.taxNumber ?? '',
    vatId: entity?.vatId ?? '',
    taxMode: entity?.taxMode ?? null,
    isActive: entity?.isActive ?? true,
    isDefault: entity?.isDefault ?? false,
  }));
  const [saving, setSaving] = useState(false);

  const text = (key: keyof BillingEntityInput) => ({
    value: (form[key] as string | null | undefined) ?? '',
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [key]: event.target.value }),
  });

  const save = async () => {
    setSaving(true);
    try {
      if (entity) {
        await billingApi.updateEntity(entity.id, form);
      } else {
        await billingApi.createEntity(form);
      }
      toast.success(entity ? 'Einheit gespeichert' : 'Einheit angelegt');
      await onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{entity ? 'Einheit bearbeiten' : 'Abrechnende Einheit anlegen'}</DialogTitle>
          <DialogDescription>
            Diese Angaben erscheinen später als Absender auf Rechnungen, Beitragsbescheiden und
            Vorabinformationen.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="entity-name">Bezeichnung im Office</Label>
            <Input id="entity-name" placeholder="z. B. Musikschule Frankfurt" {...text('name')} />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="entity-legal-name">Rechtlicher Name</Label>
            <Input id="entity-legal-name" placeholder="z. B. Musikverein Frankfurt e. V." {...text('legalName')} />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="entity-street">Straße und Hausnummer</Label>
            <Input id="entity-street" {...text('street')} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="entity-zip">PLZ</Label>
            <Input id="entity-zip" {...text('zip')} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="entity-city">Ort</Label>
            <Input id="entity-city" {...text('city')} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="entity-tax-number">Steuernummer</Label>
            <Input id="entity-tax-number" {...text('taxNumber')} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="entity-vat-id">USt-IdNr.</Label>
            <Input id="entity-vat-id" {...text('vatId')} />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="entity-tax-mode">Besteuerung</Label>
            <Select
              value={form.taxMode ?? NO_TAX_MODE}
              onValueChange={(value) =>
                setForm({ ...form, taxMode: value === NO_TAX_MODE ? null : (value as TaxMode) })
              }
            >
              <SelectTrigger id="entity-tax-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_TAX_MODE}>Noch nicht festgelegt</SelectItem>
                {Object.entries(TAX_MODE_LABEL).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-3 text-sm">
            <Switch
              checked={form.isActive ?? true}
              onCheckedChange={(checked) => setForm({ ...form, isActive: checked })}
            />
            Aktiv
          </label>
          <label className="flex items-center gap-3 text-sm">
            <Checkbox
              checked={form.isDefault ?? false}
              disabled={entity?.isDefault}
              onCheckedChange={(checked) => setForm({ ...form, isDefault: checked === true })}
            />
            Standard für Standorte ohne Zuordnung
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={save} disabled={saving || !String(form.name ?? '').trim()}>
            {saving ? 'Speichert …' : 'Speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface StripeCredentialsDialogProps {
  entity: BillingEntity;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

const STRIPE_FIELDS = [
  { key: 'secretKey', flag: 'secretKeyIsSet', label: 'Secret Key', placeholder: 'sk_live_…' },
  { key: 'publishableKey', flag: 'publishableKeyIsSet', label: 'Publishable Key', placeholder: 'pk_live_…' },
  { key: 'webhookSecret', flag: 'webhookSecretIsSet', label: 'Webhook-Secret', placeholder: 'whsec_…' },
] as const;

function StripeCredentialsDialog({ entity, onClose, onSaved }: StripeCredentialsDialogProps) {
  const flags = entity.providers?.stripe ?? {};
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const webhookPath = `/webhooks/stripe/${entity.id}`;

  const submit = async (payload: Record<string, string>) => {
    setSaving(true);
    try {
      await billingApi.saveCredentials(entity.id, 'stripe', payload);
      toast.success('Stripe-Zugangsdaten gespeichert');
      await onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const changed = Object.fromEntries(Object.entries(values).filter(([, value]) => value.trim() !== ''));

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Stripe-Konto von {entity.name}</DialogTitle>
          <DialogDescription>
            Ohne eigene Schlüssel zieht Stripe über das globale Konto ein. Gespeicherte Schlüssel werden
            verschlüsselt abgelegt und nicht wieder angezeigt.
          </DialogDescription>
        </DialogHeader>
        {flags.error && (
          <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
            Die gespeicherten Schlüssel lassen sich nicht entschlüsseln. Auf dem Server fehlt
            BILLING_ENCRYPTION_KEY oder er wurde geändert.
          </p>
        )}
        <div className="space-y-4">
          {STRIPE_FIELDS.map((field) => (
            <div key={field.key} className="space-y-2">
              <Label htmlFor={`stripe-${field.key}`} className="flex items-center gap-2">
                {field.label}
                {flags[field.flag] && <Badge variant="success" size="sm">gespeichert</Badge>}
              </Label>
              <Input
                id={`stripe-${field.key}`}
                type="password"
                autoComplete="off"
                placeholder={flags[field.flag] ? 'Leer lassen, um den Wert zu behalten' : field.placeholder}
                value={values[field.key] ?? ''}
                onChange={(event) => setValues({ ...values, [field.key]: event.target.value })}
              />
            </div>
          ))}
          <p className="text-sm text-muted-foreground">
            Webhook in Stripe auf <code className="rounded bg-muted px-1">&lt;Office-Domain&gt;{webhookPath}</code>{' '}
            einrichten, Ereignisse: payment_intent.succeeded, payment_intent.payment_failed,
            payment_intent.processing, charge.refunded, charge.dispute.created.
          </p>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            variant="outline"
            className="text-destructive"
            disabled={saving || !(flags.secretKeyIsSet || flags.publishableKeyIsSet || flags.webhookSecretIsSet)}
            onClick={() => void submit({ secretKey: '', publishableKey: '', webhookSecret: '' })}
          >
            Schlüssel entfernen
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Abbrechen
            </Button>
            <Button onClick={() => void submit(changed)} disabled={saving || Object.keys(changed).length === 0}>
              {saving ? 'Speichert …' : 'Speichern'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface DocumentSettingsDialogProps {
  entity: BillingEntity;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

/**
 * Belege und Mahnwesen je Gläubiger (ADR-0003, Phase 7): Belegart und Nummernkreis-Präfix,
 * dazu Fristen, Mahngebühren und die Stufe, ab der der Einzug ruht.
 */
function DocumentSettingsDialog({ entity, onClose, onSaved }: DocumentSettingsDialogProps) {
  const [kind, setKind] = useState<string>(entity.documentKind ?? AUTO_DOCUMENT_KIND);
  const [prefix, setPrefix] = useState(entity.documentNumberPrefix ?? '');
  const [footer, setFooter] = useState(entity.documentFooter ?? '');
  const [logoUrl, setLogoUrl] = useState(entity.brandLogoUrl ?? '');
  const [brandColor, setBrandColor] = useState(entity.brandColor ?? '');
  const [dunningEnabled, setDunningEnabled] = useState(entity.dunningEnabled ?? true);
  const [grace, setGrace] = useState(String(entity.dunningGraceDays ?? 7));
  const [interval, setInterval] = useState(String(entity.dunningIntervalDays ?? 10));
  const [payDays, setPayDays] = useState(String(entity.dunningPayDays ?? 10));
  const [blockLevel, setBlockLevel] = useState(String(entity.dunningBlockLevel ?? 3));
  const [fee1, setFee1] = useState(euroFromCents(entity.dunningFeeLevel1Cents ?? 0));
  const [fee2, setFee2] = useState(euroFromCents(entity.dunningFeeLevel2Cents ?? 300));
  const [fee3, setFee3] = useState(euroFromCents(entity.dunningFeeLevel3Cents ?? 500));
  const [consultant, setConsultant] = useState(entity.datevConsultantNumber ?? '');
  const [client, setClient] = useState(entity.datevClientNumber ?? '');
  const [chart, setChart] = useState(entity.datevChart ?? 'SKR04');
  const [revenueAccount, setRevenueAccount] = useState(entity.datevRevenueAccount ?? '');
  const [feeAccount, setFeeAccount] = useState(entity.datevFeeAccount ?? '');
  const [bankAccount, setBankAccount] = useState(entity.datevBankAccount ?? '');
  const [saving, setSaving] = useState(false);

  const logoError =
    logoUrl.trim() && !/^https?:\/\//.test(logoUrl.trim()) ? 'Adresse mit https:// angeben.' : null;
  const colorError =
    brandColor.trim() && !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(brandColor.trim())
      ? 'Hex-Wert, z. B. #5aac86.'
      : null;

  const prefixError =
    prefix.trim() && !/^[A-Za-z0-9]+(-[A-Za-z0-9]+)*$/.test(prefix.trim())
      ? 'Nur Buchstaben, Ziffern, Bindestrich.'
      : null;

  const save = async () => {
    const fees = [fee1, fee2, fee3].map(parseEuroToCents);
    if (fees.some((value) => value === null || value < 0)) {
      toast.error('Mahngebühren als Betrag angeben, z. B. 3,00');
      return;
    }
    setSaving(true);
    try {
      await billingApi.updateEntity(entity.id, {
        documentKind: kind === AUTO_DOCUMENT_KIND ? null : (kind as DocumentKind),
        documentNumberPrefix: prefix.trim() || null,
        documentFooter: footer.trim() || null,
        brandLogoUrl: logoUrl.trim() || null,
        brandColor: brandColor.trim() || null,
        dunningEnabled,
        dunningGraceDays: Number(grace),
        dunningIntervalDays: Number(interval),
        dunningPayDays: Number(payDays),
        dunningBlockLevel: Number(blockLevel),
        dunningFeeLevel1Cents: fees[0] ?? 0,
        dunningFeeLevel2Cents: fees[1] ?? 0,
        dunningFeeLevel3Cents: fees[2] ?? 0,
        datevConsultantNumber: consultant.trim() || null,
        datevClientNumber: client.trim() || null,
        datevChart: chart,
        datevRevenueAccount: revenueAccount.trim() || null,
        datevFeeAccount: feeAccount.trim() || null,
        datevBankAccount: bankAccount.trim() || null,
      });
      toast.success('Belege und Mahnwesen gespeichert');
      await onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Belege, Mahnwesen und DATEV – {entity.name}</DialogTitle>
          <DialogDescription>
            Belegart und Nummernkreis gelten je Gläubiger. Der Mahnlauf schlägt vor; freigegeben
            wird unter Finanzen → Mahnwesen.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="document-kind">Belegart</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger id="document-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={AUTO_DOCUMENT_KIND}>Nach Steuerart</SelectItem>
                <SelectItem value="invoice">Rechnung</SelectItem>
                <SelectItem value="contribution_notice">Beitragsbescheid</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Nach Steuerart: umsatzsteuerfrei nach § 4 Nr. 21 ergibt einen Beitragsbescheid,
              sonst eine Rechnung.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="document-prefix">Präfix der Belegnummer</Label>
            <Input
              id="document-prefix"
              maxLength={12}
              placeholder={kind === 'contribution_notice' ? 'BB' : 'RE'}
              value={prefix}
              onChange={(event) => setPrefix(event.target.value)}
              aria-invalid={Boolean(prefixError)}
            />
            {prefixError && <p className="text-sm text-destructive">{prefixError}</p>}
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="document-footer">Fußtext auf Beleg und Mahnung</Label>
            <Input
              id="document-footer"
              maxLength={500}
              placeholder="z. B. Bankverbindung oder Vereinsregister"
              value={footer}
              onChange={(event) => setFooter(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="document-logo">Logo auf Beleg und Mahnung</Label>
            <Input
              id="document-logo"
              maxLength={512}
              placeholder="leer = Logo aus dem Branding"
              value={logoUrl}
              onChange={(event) => setLogoUrl(event.target.value)}
              aria-invalid={Boolean(logoError)}
            />
            {logoError ? (
              <p className="text-sm text-destructive">{logoError}</p>
            ) : (
              <p className="text-xs text-muted-foreground">PNG oder JPEG; SVG lässt sich nicht ins PDF einbetten.</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="document-color">Farbe auf Beleg und Mahnung</Label>
            <div className="flex items-center gap-2">
              <Input
                id="document-color"
                maxLength={7}
                placeholder="leer = Farbe aus dem Branding"
                value={brandColor}
                onChange={(event) => setBrandColor(event.target.value)}
                aria-invalid={Boolean(colorError)}
              />
              {brandColor && !colorError ? (
                <span className="h-8 w-8 shrink-0 rounded border" style={{ backgroundColor: brandColor }} aria-hidden />
              ) : null}
            </div>
            {colorError && <p className="text-sm text-destructive">{colorError}</p>}
          </div>

          <label className="flex items-center gap-3 text-sm md:col-span-2">
            <Switch checked={dunningEnabled} onCheckedChange={setDunningEnabled} />
            Mahnlauf für diesen Gläubiger aktiv
          </label>

          <div className="space-y-2">
            <Label htmlFor="dunning-grace">Karenz nach Fälligkeit (Tage)</Label>
            <Input
              id="dunning-grace"
              type="number"
              min={0}
              max={90}
              value={grace}
              onChange={(event) => setGrace(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="dunning-interval">Abstand zwischen den Stufen (Tage)</Label>
            <Input
              id="dunning-interval"
              type="number"
              min={1}
              max={90}
              value={interval}
              onChange={(event) => setInterval(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="dunning-pay-days">Zahlungsziel der Mahnung (Tage)</Label>
            <Input
              id="dunning-pay-days"
              type="number"
              min={1}
              max={90}
              value={payDays}
              onChange={(event) => setPayDays(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="dunning-block">Einzug ruht ab Stufe</Label>
            <Select value={blockLevel} onValueChange={setBlockLevel}>
              <SelectTrigger id="dunning-block">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 – Zahlungserinnerung</SelectItem>
                <SelectItem value="2">2 – 1. Mahnung</SelectItem>
                <SelectItem value="3">3 – 2. Mahnung</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="dunning-fee1">Gebühr Zahlungserinnerung (€)</Label>
            <Input
              id="dunning-fee1"
              inputMode="decimal"
              value={fee1}
              onChange={(event) => setFee1(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="dunning-fee2">Gebühr 1. Mahnung (€)</Label>
            <Input
              id="dunning-fee2"
              inputMode="decimal"
              value={fee2}
              onChange={(event) => setFee2(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="dunning-fee3">Gebühr 2. Mahnung (€)</Label>
            <Input
              id="dunning-fee3"
              inputMode="decimal"
              value={fee3}
              onChange={(event) => setFee3(event.target.value)}
            />
          </div>

          <p className="text-sm font-medium md:col-span-2">DATEV-Export</p>
          <div className="space-y-2">
            <Label htmlFor="datev-consultant">Beraternummer</Label>
            <Input
              id="datev-consultant"
              inputMode="numeric"
              maxLength={7}
              value={consultant}
              onChange={(event) => setConsultant(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="datev-client">Mandantennummer</Label>
            <Input
              id="datev-client"
              inputMode="numeric"
              maxLength={5}
              value={client}
              onChange={(event) => setClient(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="datev-chart">Kontenrahmen</Label>
            <Select value={chart} onValueChange={setChart}>
              <SelectTrigger id="datev-chart">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="SKR03">SKR03</SelectItem>
                <SelectItem value="SKR04">SKR04</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="datev-revenue">Erlöskonto Unterricht</Label>
            <Input
              id="datev-revenue"
              inputMode="numeric"
              maxLength={9}
              placeholder={chart === 'SKR03' ? '8337' : '4337'}
              value={revenueAccount}
              onChange={(event) => setRevenueAccount(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="datev-fee">Erlöskonto Gebühren</Label>
            <Input
              id="datev-fee"
              inputMode="numeric"
              maxLength={9}
              placeholder={chart === 'SKR03' ? '8200' : '4200'}
              value={feeAccount}
              onChange={(event) => setFeeAccount(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="datev-bank">Geldkonto</Label>
            <Input
              id="datev-bank"
              inputMode="numeric"
              maxLength={9}
              placeholder={chart === 'SKR03' ? '1200' : '1800'}
              value={bankAccount}
              onChange={(event) => setBankAccount(event.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={() => void save()} disabled={saving || Boolean(prefixError || logoError || colorError)}>
            {saving ? 'Speichert …' : 'Speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface SepaDialogProps {
  entity: BillingEntity;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

const NO_CHECKOUT = 'none';
const AUTO_DOCUMENT_KIND = 'auto';

/** Cent-Betrag als Eingabewert, z. B. 300 → "3,00". */
function euroFromCents(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

function SepaDialog({ entity, onClose, onSaved }: SepaDialogProps) {
  const [creditorName, setCreditorName] = useState(entity.creditorName ?? '');
  const [creditorId, setCreditorId] = useState(entity.creditorId ?? '');
  const [iban, setIban] = useState('');
  const [bic, setBic] = useState(entity.bic ?? '');
  const [prefix, setPrefix] = useState(entity.mandateRefPrefix ?? '');
  const [days, setDays] = useState(String(entity.preNotificationDays ?? 14));
  const [checkout, setCheckout] = useState<string>(entity.checkoutProvider ?? NO_CHECKOUT);
  const [saving, setSaving] = useState(false);

  const creditorIdError = creditorId.trim() ? creditorIdProblem(creditorId) : null;
  const ibanError = iban.trim() ? ibanProblem(iban) : null;
  const daysNumber = Number(days);
  const daysError =
    !Number.isInteger(daysNumber) || daysNumber < 1 || daysNumber > 14 ? 'Zwischen 1 und 14 Tagen.' : null;
  const prefixError =
    prefix.trim() && !/^[A-Za-z0-9]+(-[A-Za-z0-9]+)*$/.test(prefix.trim()) ? 'Nur Buchstaben, Ziffern, Bindestrich.' : null;
  const invalid = Boolean(creditorIdError || ibanError || daysError || prefixError);

  const save = async () => {
    setSaving(true);
    try {
      const payload: BillingEntityInput = {
        creditorName: creditorName.trim() || null,
        creditorId: creditorId.trim() || null,
        bic: bic.trim() || null,
        mandateRefPrefix: prefix.trim() || null,
        preNotificationDays: daysNumber,
        checkoutProvider: checkout === NO_CHECKOUT ? null : (checkout as CheckoutProvider),
      };
      if (iban.trim()) payload.iban = iban.trim();
      await billingApi.updateEntity(entity.id, payload);
      toast.success('Bankverbindung gespeichert');
      await onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Bankverbindung und Lastschrift – {entity.name}</DialogTitle>
          <DialogDescription>
            Gläubigerdaten für SEPA-Lastschriften über die Hausbank. Die IBAN wird verschlüsselt gespeichert
            und danach nur noch gekürzt angezeigt.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="sepa-creditor-name">Kontoinhaber / Gläubigername</Label>
            <Input
              id="sepa-creditor-name"
              maxLength={70}
              placeholder={entity.legalName ?? entity.name}
              value={creditorName}
              onChange={(event) => setCreditorName(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sepa-creditor-id">Gläubiger-Identifikationsnummer</Label>
            <Input
              id="sepa-creditor-id"
              placeholder="DE98ZZZ09999999999"
              value={creditorId}
              onChange={(event) => setCreditorId(event.target.value)}
              aria-invalid={Boolean(creditorIdError)}
            />
            {creditorIdError && <p className="text-sm text-destructive">{creditorIdError}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="sepa-prefix">Präfix der Mandatsreferenz</Label>
            <Input
              id="sepa-prefix"
              maxLength={12}
              placeholder={`SDG${entity.id}`}
              value={prefix}
              onChange={(event) => setPrefix(event.target.value.toUpperCase())}
            />
            {prefixError ? (
              <p className="text-sm text-destructive">{prefixError}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Referenz: {(prefix.trim() || `SDG${entity.id}`).toUpperCase()}-Debitorennummer
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="sepa-iban">IBAN</Label>
            <Input
              id="sepa-iban"
              autoComplete="off"
              placeholder={entity.ibanMasked ? `Gespeichert: ${entity.ibanMasked}` : 'DE…'}
              value={iban}
              onChange={(event) => setIban(event.target.value)}
              onBlur={() => iban.trim() && !ibanError && setIban(formatIban(iban))}
              aria-invalid={Boolean(ibanError)}
            />
            {ibanError ? (
              <p className="text-sm text-destructive">{ibanError}</p>
            ) : (
              entity.ibanMasked && <p className="text-xs text-muted-foreground">Leer lassen, um die IBAN zu behalten.</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="sepa-bic">BIC (optional)</Label>
            <Input id="sepa-bic" value={bic} onChange={(event) => setBic(event.target.value.toUpperCase())} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sepa-days">Vorabinformation (Tage vor dem Einzug)</Label>
            <Input
              id="sepa-days"
              type="number"
              min={1}
              max={14}
              value={days}
              onChange={(event) => setDays(event.target.value)}
              aria-invalid={Boolean(daysError)}
            />
            {daysError ? (
              <p className="text-sm text-destructive">{daysError}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Steht im Mandatstext. Kürzer als 14 Tage nur, wenn es so im Mandat vereinbart ist.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="sepa-transport">Übertragung an die Bank</Label>
            <Input
              id="sepa-transport"
              disabled
              value={entity.sepaTransport === 'ebics' ? 'EBICS' : 'XML-Datei herunterladen und hochladen'}
            />
            <p className="text-xs text-muted-foreground">EBICS wird über „EBICS“ in der Übersicht eingerichtet.</p>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="sepa-checkout">Zahlungsart im Website-Checkout</Label>
            <Select value={checkout} onValueChange={setCheckout}>
              <SelectTrigger id="sepa-checkout">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CHECKOUT}>Nicht festgelegt</SelectItem>
                {Object.entries(CHECKOUT_PROVIDER_LABEL).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Kunden dieser Einheit zahlen auf der Website genau über diesen Weg. Hausbank setzt Gläubiger-ID
              und IBAN voraus.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={() => void save()} disabled={saving || invalid}>
            {saving ? 'Speichert …' : 'Speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
